import React from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

interface CTASectionProps {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonLink?: string;
  onButtonClick?: () => void;
}

const CTASection: React.FC<CTASectionProps> = ({
  title = "Know a marketer who needs an unfair advantage?",
  subtitle = "Share Fusion with them. You both get bonus credits. They get a system that finally gets them. Win-win-win.",
  buttonText = "Share Your Secret Weapon",
  buttonLink = "/tellafriend",
  onButtonClick
}) => {
  const navigate = useNavigate();

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    } else {
      navigate(buttonLink);
    }
  };

  return (
    <section 
      className="relative flex items-center justify-center py-20 bg-gradient-to-b from-purple-50 to-purple-100/50 overflow-hidden"
    >
      {/* Floating background orbs */}
      <motion.div
        className="absolute top-10 left-[10%] w-24 h-24 bg-purple-300/30 rounded-full blur-2xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3], y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-10 right-[15%] w-32 h-32 bg-blue-300/25 rounded-full blur-2xl"
        animate={{ scale: [1, 1.15, 1], opacity: [0.25, 0.45, 0.25], y: [0, 10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />
      <motion.div
        className="absolute top-1/2 left-[5%] w-16 h-16 bg-amber-300/20 rounded-full blur-xl"
        animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      {/* Floating sparkle icons */}
      <motion.div
        className="absolute top-16 right-[20%] text-purple-300/50"
        animate={{ y: [0, -8, 0], rotate: [0, 10, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <Sparkles className="w-6 h-6" />
      </motion.div>
      <motion.div
        className="absolute bottom-20 left-[25%] text-blue-300/50"
        animate={{ y: [0, 8, 0], rotate: [0, -10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
      >
        <Sparkles className="w-5 h-5" />
      </motion.div>

      <div className="text-center px-6 relative z-10">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 pb-2 leading-relaxed bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
        >
          {title}
        </motion.h2>
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-lg mb-8 text-foreground font-normal max-w-2xl mx-auto"
        >
          {subtitle}
        </motion.p>
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleButtonClick}
          className="relative inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold bg-purple-600 text-white transition-all duration-200 hover:bg-purple-700 overflow-hidden group"
        >
          {/* Button shimmer effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
            animate={{ x: ['-200%', '200%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
          />
          <span className="relative z-10">{buttonText}</span>
          <ArrowRight className="w-5 h-5 relative z-10" />
        </motion.button>
      </div>
    </section>
  );
};

export default CTASection;
